import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Section from '../components/ui/Section';
import SectionHeader from '../components/ui/SectionHeader';
import Card from '../components/ui/Card';

const Industries = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [expandedCards, setExpandedCards] = useState(new Set());
  
  const industries = [
    {
      name: t('industries.fintech.name'),
      description: t('industries.fintech.description'),
      outcome: t('industries.fintech.outcome'),
    },
    {
      name: t('industries.logistics.name'),
      description: t('industries.logistics.description'),
      outcome: t('industries.logistics.outcome'),
    },
    {
      name: t('industries.retail.name'),
      description: t('industries.retail.description'),
      outcome: t('industries.retail.outcome'),
    },
    {
      name: t('industries.public.name'),
      description: t('industries.public.description'),
      outcome: t('industries.public.outcome'),
    },
  ];

  const toggleCard = (industryName: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(industryName)) {
        newSet.delete(industryName);
      } else {
        newSet.add(industryName);
      }
      return newSet;
    });
  };

  return (
    <Section id="industries" withFlyingElements={true}>
      <SectionHeader
        title={t('industries.title')}
        subtitle={t('industries.subtitle')}
        withDivider={true}
      />
      <div className="grid md:grid-cols-2 gap-6">
        {industries.map((industry, index) => {
          const isExpanded = expandedCards.has(industry.name);
          
          return (
            <motion.div
              key={industry.name}
              initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="relative group"
            >
              {/* Glowing background effect */}
              <motion.div
                className={`absolute -inset-0.5 rounded-xl blur-md ${
                  isDark ? 'bg-afsc-gold/40' : 'bg-afsc-gold/30'
                }`}
                animate={isExpanded ? {
                  opacity: [0.4, 0.7, 0.4],
                } : {
                  opacity: 0,
                }}
                whileHover={!isExpanded ? {
                  opacity: 0.6,
                } : {}}
                transition={{
                  duration: isExpanded ? 2 : 0.3,
                  repeat: isExpanded ? Infinity : 0,
                  ease: 'easeInOut',
                }}
              />
              
              <Card 
                className={`relative ${
                  isDark ? 'bg-white/5' : 'bg-white/60'
                } backdrop-blur-sm border ${
                  isDark 
                    ? isExpanded ? 'border-afsc-gold/50' : 'border-afsc-soft-gray/20' 
                    : isExpanded ? 'border-afsc-gold/50' : 'border-afsc-charcoal/10'
                } hover:border-afsc-gold/50 cursor-pointer transition-all duration-300`}
                style={isExpanded ? {
                  boxShadow: isDark
                    ? '0 0 25px rgba(203, 163, 90, 0.25), 0 0 50px rgba(203, 163, 90, 0.15)'
                    : '0 0 25px rgba(203, 163, 90, 0.2), 0 0 50px rgba(203, 163, 90, 0.1)',
                } : {}}
              >
                <div
                  onClick={() => toggleCard(industry.name)}
                  className="flex items-center justify-between py-2"
                >
                  <h3 className={`text-2xl font-heading font-bold ${
                    isDark ? 'text-white' : 'text-afsc-charcoal'
                  }`}>
                    {industry.name}
                  </h3>
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 ${
                      isDark 
                        ? 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white' 
                        : 'bg-afsc-charcoal/10 text-afsc-charcoal/70 hover:bg-afsc-charcoal/20 hover:text-afsc-charcoal'
                    }`}
                  >
                    <svg 
                      className="w-5 h-5" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M19 9l-7 7-7-7" 
                      />
                    </svg>
                  </motion.div>
                </div>
                
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="pt-6 space-y-4">
                        <motion.p 
                          initial={{ y: -10, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.1, duration: 0.3 }}
                          className={`text-base leading-relaxed ${isDark ? 'text-white/80' : 'text-afsc-charcoal/70'}`}
                        >
                          {industry.description}
                        </motion.p>
                        <motion.div 
                          initial={{ y: -10, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.2, duration: 0.3 }}
                          className={`pt-4 border-t ${
                            isDark ? 'border-white/10' : 'border-afsc-charcoal/10'
                          }`}
                        >
                          <p className="text-sm text-afsc-gold font-semibold">
                            {industry.outcome}
                          </p>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
};

export default Industries;

